import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { InferRequestType, InferResponseType } from "hono/client"
import { client, APIError } from "@/api"

// Admin compose (v1): send one plain-text email from an allowlisted @playside.co
// identity through the normal SES path, logged to email_events. Mirrors
// backend/src/lib/admin-compose.ts.
//
// Both the request and response shapes are INFERRED from the route rather than
// redeclared. Hand-writing them would silently drop the backend's guarantees —
// `from` would widen from the allowlist union to `string`, and a rename like
// `name` → `displayName` would typecheck green while the picker rendered
// `undefined` for every option.

export type SendComposedPayload = InferRequestType<
  typeof client.admin.email.send.$post
>["json"]

/** The allowlisted addresses, as a type — useful for narrowing UI state. */
export type ComposeSenderAddress = SendComposedPayload["from"]

type SendersResponse = InferResponseType<typeof client.admin.email.senders.$get>
export type ComposeSender = SendersResponse["senders"][number]

/**
 * The identities the backend will accept as `from`. Fetched rather than
 * hardcoded so the picker can never offer an address the API would reject —
 * the allowlist has exactly one source of truth (admin-compose.ts).
 */
export function useComposeSenders() {
  return useQuery({
    queryKey: ["admin", "email", "senders"],
    // The RPC client throws APIError on any non-ok response (see api/index.ts),
    // so there is no !res.ok branch to write here.
    queryFn: async () => (await client.admin.email.senders.$get()).json(),
    // Static allowlist — no need to refetch during a session.
    staleTime: Infinity,
  })
}

export function useSendComposedEmail() {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean; messageId: string | null }, APIError, SendComposedPayload>({
    mutationFn: async (payload) => {
      const res = await client.admin.email.send.$post({ json: payload })
      return (await res.json()) as { ok: boolean; messageId: string | null }
    },
    onSuccess: () => {
      // The send just wrote an email_events row — refresh the Log and Stats tabs
      // so it shows up without a manual reload. Both keys are PREFIXES of the
      // real ones (["admin","email-log",filters] / ["admin","email-stats",days]),
      // which is what makes the partial match work.
      qc.invalidateQueries({ queryKey: ["admin", "email-log"] })
      qc.invalidateQueries({ queryKey: ["admin", "email-stats"] })
      // A send that started from a response changes that person's status on the Events tab
      // ("Email sent"). Mark it stale so going Back to the tab within its 30 s freshness
      // window refetches instead of offering a second send from a cached row.
      qc.invalidateQueries({ queryKey: ["admin", "response-events"] })
    },
  })
}
